<template>
  <div class="flex mb-12 video-form">
    <div class="w-3/5 content-left-panel">
      <video-form-content
        ref="videoFormContent"
        :embedded="false"
        :onClick="saveVideo"
        @prevent-close="rememberToSaveFiles = $event"
      />

      <ContentTable :data="currentVideoCourses" type="Courses" />

      <ContentTable :data="currentVideoLPaths" type="Learning Paths" />
    </div>
    <div class="w-2/5 pl-10 content-right-panel" sticky-container>
      <div v-sticky sticky-offset="{top: 25}" sticky-z-index="999">
        <div class="flex mb-10 -mx-2" v-if="isEditing">
          <div class="w-1/2 mx-2 modified-date">
            <label class="vs-input--label">Modified on</label>
            <vs-input
              disabled
              :value="getCurrentVideo.modified | dateFormat"
            ></vs-input>
          </div>
          <div class="w-1/2 mx-2 created-date">
            <label class="vs-input--label">Created on</label>
            <vs-input
              disabled
              :value="getCurrentVideo.created | dateFormat"
            ></vs-input>
          </div>
        </div>
        <div class="flex -mx-2 action-buttons">
          <vs-button
            class="w-full mx-2"
            color="primary"
            icon-pack="feather"
            @click="saveVideo"
            >Save</vs-button
          >
        </div>
        <div class="mt-10 video-preview" v-if="isEditing">
          <div v-if="getCurrentVideo.jwplayer_id">
            <video-preview :mediaId="getCurrentVideo.jwplayer_id" />
            <div class="mt-5">
              <i
                ><a
                  :href="`https://dashboard.jwplayer.com/#/content/detail?key=${getCurrentVideo.jwplayer_id}`"
                  target="_blank"
                  >Go to this video in dashboard.jwplayer.com</a
                ></i
              >
            </div>
          </div>
        </div>
        <div v-if="!localLoading" class="mt-10">
          <h4>Related content</h4>
          <RelatedContentList
            :metadata="getCurrentVideo.metadata"
            :allowedParents="['courses', 'learning_paths']"
          />
        </div>
      </div>
    </div>
    <UnsavedChangesPopup
      :active="showUnsavedChangesModal"
      @close="closeUnsavedChangesModal"
      @save="saveAndLeaveFromUnsavedChangesModal"
      @discard="discardAndLeaveFromUnsavedChangesModal"
    />
  </div>
</template>

<script>
import Sticky from 'vue-sticky-directive'
import cloneDeep from 'lodash.clonedeep'
import isEqual from 'lodash.isequal'
import videoFormContent from './forms/VideoFormContent'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import VideoPreview from '@/components/VideoPreview'
import ContentTable from './components/ContentTable'
import {
  getBasicData,
  buildUnsavedChangesNavigationHandlers,
  unsavedChangesNavigation,
} from '@/utils/mixins.js'
import RelatedContentList from './components/RelatedContentList.vue'
import UnsavedChangesPopup from '@/components/UnsavedChangesPopup.vue'
export default {
  components: {
    videoFormContent,
    VideoPreview,
    ContentTable,
    RelatedContentList,
    UnsavedChangesPopup,
  },
  mixins: [getBasicData, unsavedChangesNavigation],
  data: () => {
    return {
      currentVideoCourses: {
        count: 0,
        results: [],
      },
      currentVideoLPaths: {
        count: 0,
        results: [],
      },
      localLoading: null,
      persistedVideoSnapshot: null,
      rememberToSaveFiles: false,
    }
  },
  directives: { Sticky },
  async created() {
    if (this.$route && this.$route.params.id) {
      if (this.$route.params.id !== this.getCurrentVideo.id) {
        this.getVideoToEdit(this.$route.params.id)
      } else {
        this.setPersistedVideoSnapshot(this.getCurrentVideo)
      }
    } else {
      this.resetCurrentVideo()
    }
  },
  async mounted() {
    // @mixin
    await this.getBasicData()
  },
  computed: {
    ...mapGetters('app', ['getSelectedCompanyName']),
    ...mapGetters('videos', ['getCurrentVideo']),
    ...mapGetters('taxonomy', ['getVideoTaxonomy']),
    isEditing() {
      return this.$route.name === 'edit-video'
    },
  },
  methods: {
    ...mapMutations('videos', {
      storeCurrentVideo: 'STORE_CURRENT_VIDEO',
    }),
    ...mapMutations('taxonomy', {
      clearVideoTaxonomy: 'CLEAR_VIDEO_TAXONOMY',
    }),
    ...mapActions('videos', [
      'getVideoAction',
      'resetCurrentVideo',
      'saveVideoToApi',
      'assignTaxonomiesToVideo',
    ]),
    ...mapActions('courses', ['searchCourses']),
    ...mapActions('lpaths', ['searchLPaths']),
    setPersistedVideoSnapshot(video = this.getCurrentVideo) {
      this.persistedVideoSnapshot = cloneDeep(video || {})
    },
    hasPendingVideoTaxonomyChanges() {
      return (
        this.getVideoTaxonomy &&
        this.getVideoTaxonomy.contentId === this.getCurrentVideo.id &&
        Array.isArray(this.getVideoTaxonomy.addedSkills) &&
        this.getVideoTaxonomy.addedSkills.length > 0
      )
    },
    getVideoDraftForDirtyCheck() {
      if (
        this.$refs.videoFormContent &&
        this.$refs.videoFormContent.localVideo
      ) {
        return this.$refs.videoFormContent.localVideo
      }
      return this.getCurrentVideo
    },
    hasUnsavedVideoChanges() {
      if (!this.isEditing) return false
      if (!this.persistedVideoSnapshot) return false
      if (this.rememberToSaveFiles) return true
      if (this.hasPendingVideoTaxonomyChanges()) return true

      return !isEqual(
        this.getVideoDraftForDirtyCheck(),
        this.persistedVideoSnapshot
      )
    },
    ...buildUnsavedChangesNavigationHandlers({
      hasUnsavedChangesMethod: 'hasUnsavedVideoChanges',
      saveForNavigationMethod: 'saveVideo',
      restoreForDiscardMethod: 'restoreVideoSnapshotForDiscard',
    }),
    restoreVideoSnapshotForDiscard() {
      if (!this.persistedVideoSnapshot) return
      const restoredVideo = cloneDeep(this.persistedVideoSnapshot)
      this.storeCurrentVideo(restoredVideo)
      this.clearVideoTaxonomy({ contentId: restoredVideo.id })
      if (
        this.$refs.videoFormContent &&
        typeof this.$refs.videoFormContent.resetLocalVideo === 'function'
      ) {
        this.$refs.videoFormContent.resetLocalVideo(restoredVideo)
      }
      this.rememberToSaveFiles = false
    },
    applyRouteLeaveState(to) {
      if (to.name === 'create-video') {
        this.resetCurrentVideo()
      } else if (to.name === 'edit-video') {
        this.getVideoToEdit(to.params.id)
      }
    },
    async saveTaxonomies() {
      try {
        if (!this.hasPendingVideoTaxonomyChanges()) return true

        const addedSkills =
          this.getVideoTaxonomy &&
          this.getVideoTaxonomy.contentId === this.getCurrentVideo.id
            ? this.getVideoTaxonomy.addedSkills
            : []

        await this.assignTaxonomiesToVideo({
          contentId: this.getCurrentVideo.id,
          addedSkills,
        })

        this.clearVideoTaxonomy({ contentId: this.getCurrentVideo.id })
        return true
      } catch (error) {
        return false
      }
    },
    async saveVideo() {
      let didSave = false
      try {
        this.$vs.loading()

        if (
          this.$refs.videoFormContent &&
          typeof this.$refs.videoFormContent.syncLocalVideoToStore === 'function'
        ) {
          this.$refs.videoFormContent.syncLocalVideoToStore()
        }

        if (this.$route.name !== 'create-video') {
          const didSaveTaxonomies = await this.saveTaxonomies()
          if (!didSaveTaxonomies) {
            this.showError(
              'Some taxonomy changes could not be saved. Please try again.'
            )
            return false
          }
        }

        const response = await this.saveVideoToApi()

        if (
          this.$refs.videoFormContent &&
          typeof this.$refs.videoFormContent.syncPersistedCustomContentAudienceType ===
            'function'
        ) {
          this.$refs.videoFormContent.syncPersistedCustomContentAudienceType(
            response.audience_type
          )
        }

        if (this.$route.name === 'create-video') {
          this.$router.push({ name: 'edit-video', params: { id: response.id } })
        }

        this.$vs.notify({
          title: 'Success',
          text: 'Video saved',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'success',
        })
        this.setPersistedVideoSnapshot(this.getCurrentVideo)
        this.rememberToSaveFiles = false

        if (
          this.$refs.videoFormContent &&
          typeof this.$refs.videoFormContent.markFilesAsSaved === 'function'
        ) {
          this.$refs.videoFormContent.markFilesAsSaved()
        }

        didSave = true
      } catch (error) {
        this.showError(error)
      } finally {
        this.$vs.loading.close()
      }
      return didSave
    },
    async getVideoToEdit(videoId) {
      this.$vs.loading()
      this.localLoading = true
      try {
        await this.getVideoAction({ videoId: videoId })
        if (
          this.getCurrentVideo.metadata.company !==
          this.getSelectedCompanyName.toLowerCase()
        ) {
          // ToDo: Possible bug, we could still be in create
          this.$router.push({ name: 'create-video' })
          this.$vs.notify({
            title: 'Warning',
            text: 'Video you were editing belongs to a different company',
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'warning',
          })
        }
        // Get courses associated to video
        if (
          this.getCurrentVideo.metadata.courses &&
          this.getCurrentVideo.metadata.courses.length
        ) {
          const response = await this.searchCourses({
            ids: this.getCurrentVideo.metadata.courses,
          })
          this.currentVideoCourses.count = response.count
          this.currentVideoCourses.results = response.results
        }
        // Get LPaths associated to video
        if (
          this.getCurrentVideo.metadata.learning_paths &&
          this.getCurrentVideo.metadata.learning_paths.length
        ) {
          const response = await this.searchLPaths({
            ids: this.getCurrentVideo.metadata.learning_paths,
          })
          this.currentVideoLPaths.count = response.count
          this.currentVideoLPaths.results = response.results
        }
      } catch (error) {
        this.showError(error)
      } finally {
        this.setPersistedVideoSnapshot(this.getCurrentVideo)
        this.localLoading = false
        this.$vs.loading.close()
      }
    },
  },
  beforeRouteLeave(to, from, next) {
    this.handleUnsavedChangesBeforeRouteLeave(to, next)
  },
}
</script>
<style lang="scss" scoped></style>
